import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChartTheme } from "@/hooks/useChartTheme";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SalesStatusBadge } from "@/components/sales/SalesStatusBadge";
import { SettleSuggestionsCard } from "@/components/sales/SettleSuggestionsCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatKr, formatDate, formatMonth, daysLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Plus, Receipt, AlertTriangle, CircleDollarSign, FileText, TrendingUp, Contact, ArrowRight, BadgeCheck, Wallet, BellRing, Repeat } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";

const Kpi = ({ label, value, sub, icon: Icon, tone, to, index, testId }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: index * 0.04, ease: [0.2, 0.8, 0.2, 1] }}>
    <Link to={to} className="group block rounded-xl border bg-card p-4 card-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0", tone)}><Icon style={{ width: 15, height: 15 }} /></span>
        <p className="text-sm font-medium text-muted-foreground truncate">{label}</p>
      </div>
      <p className="mt-3 font-heading text-2xl sm:text-3xl font-semibold tnum leading-none whitespace-nowrap">{value}</p>
      <p className="mt-1.5 text-xs text-muted-foreground tnum">{sub}</p>
    </Link>
  </motion.div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium">{formatMonth(label)}</p>
      <p className="text-muted-foreground">Fakturert eks. mva: <span className="text-foreground tnum">{formatKr(d.net)}</span></p>
      <p className="text-muted-foreground">Innbetalt: <span className="text-foreground tnum">{formatKr(d.paid)}</span></p>
    </div>
  );
};

export default function SalesDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const chart = useChartTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/sales/dashboard").then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <EmptyState title="Kunne ikke laste oversikten" description={error} actionLabel="Prøv igjen" onAction={() => window.location.reload()} />;
  if (!data) return <div className="space-y-6"><Skeleton className="h-12 w-72" /><div className="grid gap-4 grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div><Skeleton className="h-80" /></div>;

  const k = data.kpis;
  const isEmpty = data.recent.length === 0;
  const firstName = (user?.full_name || "").split(" ")[0];

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        eyebrow="Faktura"
        title={`Fakturering${firstName ? `, ${firstName}` : ""}`}
        description={k.utestaende.count ? `${k.utestaende.count} ${k.utestaende.count === 1 ? "faktura" : "fakturaer"} venter på betaling – totalt ${formatKr(k.utestaende.sum)}.` : "Ingen ubetalte fakturaer akkurat nå."}
        actions={
          <>
            <Button variant="ghost" asChild className="hidden sm:inline-flex" data-testid="sales-dashboard-customers-button"><Link to="/faktura/kunder">Kunder</Link></Button>
            <Button variant="ghost" asChild className="hidden sm:inline-flex" data-testid="sales-dashboard-recurring-button"><Link to="/faktura/gjentakende"><Repeat className="h-4 w-4 mr-2" /> Gjentakende{data.recurring_active ? ` (${data.recurring_active})` : ""}</Link></Button>
            <Button asChild data-testid="sales-dashboard-new-invoice-button"><Link to="/faktura/fakturaer/ny"><Plus className="h-4 w-4 mr-2" /> Ny faktura</Link></Button>
          </>
        }
      />

      {!data.profile_completeness.complete ? (
        <Alert className="border-[#FFE2A3] dark:border-[#8A5A00]/50" data-testid="sales-profile-incomplete-alert">
          <BadgeCheck className="h-4 w-4" />
          <AlertTitle>Fullfør firmaprofilen før du utsteder fakturaer</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Mangler: {data.profile_completeness.missing.join(", ")}.</span>
            <Button variant="link" className="h-auto p-0" onClick={() => navigate("/profil")} data-testid="sales-complete-profile-link">Gå til profil <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {data.recurring_pending > 0 ? (
        <Alert className="border-primary/30" data-testid="sales-recurring-pending-alert">
          <Repeat className="h-4 w-4" />
          <AlertTitle>{data.recurring_pending} fakturakladd{data.recurring_pending === 1 ? "" : "er"} fra gjentakende avtaler venter på godkjenning</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Kontroller og utsted dem i én omgang.</span>
            <Button variant="link" className="h-auto p-0" onClick={() => navigate("/faktura/gjentakende")} data-testid="sales-recurring-pending-link">Gå til Gjentakende <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Kpi index={0} label="Utestående" value={formatKr(k.utestaende.sum)} sub={`${k.utestaende.count} ${k.utestaende.count === 1 ? "faktura" : "fakturaer"}`} icon={Wallet} tone="kpi-icon-til_betaling" to="/faktura/fakturaer?status=apen" testId="sales-kpi-outstanding" />
        <Kpi index={1} label="Forfalt" value={formatKr(k.forfalt.sum)} sub={k.forfalt.count ? `${k.forfalt.count} over forfall` : "Ingen forfalte"} icon={AlertTriangle} tone="kpi-icon-forfalt" to="/faktura/fakturaer?status=forfalt" testId="sales-kpi-overdue" />
        <Kpi index={2} label="Innbetalt denne måneden" value={formatKr(k.betalt_mnd.sum)} sub="Registrerte betalinger" icon={CircleDollarSign} tone="kpi-icon-betalt" to="/faktura/fakturaer?status=betalt" testId="sales-kpi-paid-month" />
        <Kpi index={3} label="Kladder" value={String(k.kladder.count)} sub={k.kladder.count ? "Ikke utstedt ennå" : "Ingen kladder"} icon={FileText} tone="kpi-icon-ma_kontrolleres" to="/faktura/fakturaer?status=kladd" testId="sales-kpi-drafts" />
      </div>

      {data.settle_suggestions?.count ? <SettleSuggestionsCard initial={data.settle_suggestions} onChanged={load} /> : null}

      {isEmpty ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <EmptyState icon={Receipt} title="Send din første faktura" description="Legg inn en kunde, velg hva du har levert, og Controlle lager en profesjonell faktura med riktig MVA, fakturanummer og betalingsinformasjon." actionLabel="Lag faktura" actionTo="/faktura/fakturaer/ny" testId="sales-empty-state" />
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Contact className="h-4 w-4 text-primary" /> Kom i gang</CardTitle><CardDescription>Tre steg til første faktura.</CardDescription></CardHeader>
            <CardContent>
              <ol className="space-y-3 text-sm">
                {[["Fullfør firmaprofilen", "Navn, org.nr, adresse, kontonummer og logo.", "/profil", data.profile_completeness.complete], ["Legg inn en kunde", "Firma eller privatperson med e-post for sending.", "/faktura/kunder", data.customer_count > 0], ["Lag og utsted fakturaen", "Linjer med MVA, forfall og referanser. Last ned PDF eller send på e-post.", "/faktura/fakturaer/ny", false]].map(([t, d, to, done], i) => (
                  <li key={t} className="flex gap-3">
                    <span className={cn("h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0", done ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>{i + 1}</span>
                    <div><Link to={to} className="font-medium hover:underline">{t}</Link><p className="text-muted-foreground">{d}</p></div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Omsetning siste 6 måneder</CardTitle><CardDescription>Fakturert eks. mva (etter kreditnotaer) og innbetalt.</CardDescription></div>
                  <div className="text-right"><p className="text-xs text-muted-foreground">Hittil i år</p><p className="font-heading font-semibold tnum whitespace-nowrap" data-testid="sales-ytd">{formatKr(k.omsetning_ytd.sum)}</p></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-64" data-testid="sales-revenue-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.revenue} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={4}>
                      <CartesianGrid stroke={chart.grid} vertical={false} />
                      <XAxis dataKey="month" tickFormatter={(m) => formatMonth(m, true)} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                      <RTooltip content={<ChartTooltip />} cursor={{ fill: chart.cursor }} />
                      <Bar dataKey="net" name="Fakturert" fill={chart.primary} radius={[6, 6, 0, 0]} maxBarSize={28} />
                      <Bar dataKey="paid" name="Innbetalt" fill={chart.primarySoft} radius={[6, 6, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex gap-5 pt-2 text-xs text-muted-foreground">
                  <li className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: chart.primary }} /> Fakturert eks. mva</li>
                  <li className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: chart.primarySoft }} /> Innbetalt</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-flag" /> Forfalte fakturaer</CardTitle><CardDescription>{data.overdue.length ? "Vurder å sende purring." : "Ingen forfalte fakturaer."}</CardDescription></div>
                  {data.reminder_suggestions > 0 ? (
                    <Button size="sm" asChild data-testid="sales-dashboard-reminders-cta"><Link to="/faktura/purringer"><BellRing className="h-4 w-4 mr-1.5" /> {data.reminder_suggestions} klar{data.reminder_suggestions === 1 ? "" : "e"} for purring</Link></Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {data.overdue.length === 0 ? (
                  <p className="px-6 pb-6 text-sm text-muted-foreground">Alt er à jour.</p>
                ) : (
                  <ul className="divide-y">
                    {data.overdue.map((i) => (
                      <li key={i.id}>
                        <Link to={`/faktura/fakturaer/${i.id}`} className="flex items-center gap-3 px-4 sm:px-6 py-3 hover:bg-secondary/60 transition-colors" data-testid={`sales-overdue-${i.id}`}>
                          <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{i.customer?.name}</p><p className="text-xs text-flag">{i.number_display} · {daysLabel(i.days_to_due)}</p></div>
                          <p className="text-sm font-medium tnum whitespace-nowrap">{formatKr(i.outstanding)}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
            <Card>
              <CardHeader className="pb-2 flex-row flex-wrap items-start justify-between gap-2">
                <div className="min-w-0"><CardTitle className="text-base">Siste aktivitet</CardTitle><CardDescription>Nylig endrede fakturaer og kreditnotaer.</CardDescription></div>
                <Button variant="ghost" size="sm" asChild className="shrink-0"><Link to="/faktura/fakturaer" data-testid="sales-see-all-link">Se alle <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link></Button>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y">
                  {data.recent.map((i) => (
                    <li key={i.id}>
                      <Link to={`/faktura/fakturaer/${i.id}`} className="flex items-center gap-3 px-4 sm:px-6 py-3 hover:bg-secondary/60 transition-colors" data-testid={`sales-recent-${i.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{i.customer?.name || "Uten kunde"}</p>
                          <p className="text-xs text-muted-foreground truncate">{i.type === "kreditnota" ? "Kreditnota" : "Faktura"} {i.number_display || "(kladd)"} · {formatDate(i.issue_date)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <p className="text-sm font-medium tnum whitespace-nowrap">{formatKr(i.total)}</p>
                          <SalesStatusBadge status={i.display_status} type={i.type} />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Contact className="h-4 w-4 text-primary" /> Kunder med utestående</CardTitle><CardDescription>{data.customer_count} aktive kunder</CardDescription></CardHeader>
              <CardContent className="p-0">
                {data.top_customers.length === 0 ? <p className="px-6 pb-6 text-sm text-muted-foreground">Ingen utestående beløp.</p> : (
                  <ul className="divide-y">
                    {data.top_customers.map((c) => (
                      <li key={c.customer_id} className="flex items-center gap-3 px-6 py-3">
                        <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground">{c.count} {c.count === 1 ? "faktura" : "fakturaer"}</p></div>
                        <p className="text-sm font-medium tnum whitespace-nowrap">{formatKr(c.outstanding)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
